import i18n from './i18n';
import iconParagraph from '@adsk/bim360-matrix-react-assets/icon-custom-attribute-paragraph.svg';
import iconText from '@adsk/bim360-matrix-react-assets/icon-custom-attribute-text.svg';
import iconSingleSelect from '@adsk/bim360-matrix-react-assets/icon-custom-attribute-single-select.svg';
import iconNumeric from '@adsk/bim360-matrix-react-assets/icon-custom-attribute-numeric.svg';
import lodashInvoke from 'lodash/invoke';
import {
  ISSUE_ATTRIBUTE_MAX_DESCRIPTION_LENGTH,
  ISSUE_ATTRIBUTE_MAX_LIST_ITEM_TEXT_LENGTH,
  ISSUE_ATTRIBUTES_DATA_TYPES,
  ISSUE_ATTRIBUTES_PERMITTED_ACTIONS,
  ISSUE_ATTRIBUTE_MAX_TITLE_LENGTH,
  ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES
} from '../constants/issue-attributes';
import {presenceValidator} from './validation-helpers';
import {ISSUE_ATTRIBUTE_NAMES} from '../constants/issue-attribute-names';

export const getDataTypeName = dataType => {
  switch (dataType) {
    default:
    case ISSUE_ATTRIBUTES_DATA_TYPES.TEXT:
      return i18n.t('issueAttributesAdmin.dataTypes.text');
    case ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST:
      return i18n.t('issueAttributesAdmin.dataTypes.singleList');
    case ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC:
      return i18n.t('issueAttributesAdmin.dataTypes.numeric');
    case ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH:
      return i18n.t('issueAttributesAdmin.dataTypes.paragraph');
  }
};

export const getIconByDataType = dataType => {
  switch (dataType) {
    default:
    case ISSUE_ATTRIBUTES_DATA_TYPES.TEXT:
      return iconText;
    case ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST:
      return iconSingleSelect;
    case ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH:
      return iconParagraph;
    case ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC:
      return iconNumeric;
  }
};

export const shouldShowValues = ({dataType, metadata}) =>
  dataType === ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST &&
  metadata[dataType].options &&
  metadata[dataType].options.length > 0;

export const issueAttributeTitleValidators = () => [
  {
    message: i18n.t('issueAttributesAdmin.modal.maxCharactersLimit', {
      max: ISSUE_ATTRIBUTE_MAX_TITLE_LENGTH
    }),
    validate: text => !text || text.length < ISSUE_ATTRIBUTE_MAX_TITLE_LENGTH
  },
  {
    message: i18n.t('issueAttributesAdmin.modal.required'),
    validate: presenceValidator
  }
];

export const isIssueAttributeTitleValid = value =>
  issueAttributeTitleValidators()
    .map(validator => validator.validate(value))
    .every(isValid => isValid);

export const isIssueAttributeDescriptionValid = text => !text || text.length < ISSUE_ATTRIBUTE_MAX_DESCRIPTION_LENGTH;

export const issueAttributeListItemValidators = options => [
  {
    message: i18n.t('issueAttributesAdmin.modal.maxCharactersLimit', {
      max: ISSUE_ATTRIBUTE_MAX_LIST_ITEM_TEXT_LENGTH
    }),
    validate: text => !text || text.length < ISSUE_ATTRIBUTE_MAX_LIST_ITEM_TEXT_LENGTH
  },
  {
    message: i18n.t('issueAttributesAdmin.modal.listItemExists'),
    validate: text => !text || !options.includes(text.trim().toLowerCase())
  },
  {
    message: i18n.t('issueAttributesAdmin.modal.required'),
    validate: presenceValidator
  }
];

export const isIssueAttributeListItemValid = (value, options) =>
  issueAttributeListItemValidators(options)
    .map(validator => validator.validate(value))
    .every(isValid => isValid);

export const isIssueAttributeListItemsValid = options =>
  // We allow the last option val to be empty.
  // This check is needed because clicking the disabled create button doesn't trigger the validation running in IssueAttributesAdminDraggableList
  // and we need to identify this case also here.
  options.length > 0 &&
  options.every(
    (option, index) => option.valid || (options.length > 1 && index === options.length - 1 && option.value === '')
  );

export const prepareIssueAttributeOptionsForBackend = (options, dataType) => ({[dataType]: {options}});

export const isInheritedAttribute = (issueTypeAttribute, isSubType, isContainerType) =>
  issueTypeAttribute &&
  ((!isContainerType && issueTypeAttribute.mappedItemType === ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES.CONTAINER) ||
    (isSubType && issueTypeAttribute.mappedItemType !== ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES.SUBTYPE));

export const canEditIssueAttribute = ({permittedActions}) =>
  permittedActions && permittedActions.includes(ISSUE_ATTRIBUTES_PERMITTED_ACTIONS.EDIT);
export const canDeleteIssueAttribute = ({permittedActions}) =>
  permittedActions && permittedActions.includes(ISSUE_ATTRIBUTES_PERMITTED_ACTIONS.DELETE);

export const flattenIssueAttributesResults = results => {
  const ids = [];
  const entitiesMap = results.reduce((acc, result) => {
    acc[result.id] = result;
    ids.push(result.id);
    return acc;
  }, {});

  return {entitiesMap, ids};
};

export const updateValueInCustomAttributesArray = (currentCustomAttributes, customAttributeId, newValue) => {
  const customAttributes = currentCustomAttributes.map(({attributeDefinitionId, value = null}) => ({
    attributeDefinitionId,
    value
  }));
  const attributeIndex = customAttributes.findIndex(
    attribute => attribute && attribute.attributeDefinitionId === customAttributeId
  );
  const valueObj = {attributeDefinitionId: customAttributeId, value: newValue};
  if (attributeIndex !== -1) {
    customAttributes[attributeIndex] = valueObj;
  } else {
    customAttributes.push(valueObj);
  }
  return customAttributes;
};

const getAttributeValue = (attributeDefinition, customAttribute) => {
  const {metadata, dataType} = attributeDefinition;
  switch (dataType) {
    case ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST: {
      const {options} = metadata.list;
      const selectedOption = customAttribute && options.find(option => option.id === customAttribute.value);
      return selectedOption ? selectedOption.value : null;
    }
    case ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH:
    case ISSUE_ATTRIBUTES_DATA_TYPES.TEXT:
    case ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC:
    default: {
      return customAttribute ? customAttribute.value : null;
    }
  }
};

export const getAttributeValueFromIssue = (attributeDefinition, issue) =>
  getAttributeValue(
    attributeDefinition,
    issue[ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES].find(ca => ca.attributeDefinitionId === attributeDefinition.id)
  );

export const getAttributeValueFromTemplate = (attributeDefinition, issueTemplate) =>
  getAttributeValue(
    attributeDefinition,
    lodashInvoke(
      issueTemplate,
      'issueAttributes.customAttributes.find',
      ca => ca.attributeDefinitionId === attributeDefinition.id
    )
  );
